import { FC, useMemo, useState } from "react";
import {
  Boosting,
  BoostingCampaign,
  Campaign,
} from "@vviinn/user-managment-client-ts";

import { useAppSelector } from "../../../store";
import { Fields } from "../../../ui/layout/detailedPages/Fields";
import { NamedRow } from "../../../components/GeneralUI/NamedRow/NamedRow";
import { SlideOverWrapper } from "../../../ui/layout/organisms/SlideOver/SlideOverWrapper/SlideOverWrapper";
import { SearchBox } from "../../../components/Interaction/SearchBox/SearchBox";
import { CollapsedFields } from "../../../components/GeneralUI/CollapsedFields/CollapsedFields";
import { useSlideOverSearch } from "../../../ui/layout/organisms/SlideOver/hooks/useSlideOverSearch";
import { WeightNumberInput } from "./WeightNumberInput";
import { campaignTypeTitles } from "../../constants";
import { BOOSTING_ELIGIBLE_CAMPAIGN_TYPES } from "../constants";
import { handleCampaign } from "../../../store/features/campaigns/handleCampaign";
import { useRequestHandlers } from "../../../app/hooks/useRequestHandlers";
import { compareAsStrings } from "../../../helpers";

const EDIT_PERMISSIONS = "campaign:basic_config:update";

type LinkedServiceDraft = { campaignId: string; weight: number };
type LinkedServiceRowDraft = LinkedServiceDraft & { active: boolean };

const weightForBoosting = (campaign: Campaign, boostingId?: string) =>
  campaign.boosting?.linkedBoostingCampaigns?.find(
    (lbc) => lbc.campaign?.id === boostingId,
  )?.weight ?? 0;

const buildLinkedServiceDrafts = (
  campaigns: Campaign[],
  boostingId?: string,
): LinkedServiceDraft[] =>
  campaigns.map((c) => ({
    campaignId: c.id ?? "",
    weight: weightForBoosting(c, boostingId),
  }));

// The fixed row list: every type-eligible campaign, plus any campaign already linked to
// this boosting campaign even if its type later fell out of BOOSTING_ELIGIBLE_CAMPAIGN_TYPES
// — it must stay visible/unlinkable rather than silently disappear from the list. Order is
// linked-first then alphabetical, and is static for the session — it's derived from the
// persisted linkedServiceCampaigns prop, not the live draft state, so toggling a switch
// never reshuffles rows out from under the user while the slide-over is open.
const eligibleCampaignsForRows = (
  allCampaigns: Campaign[],
  linkedServiceCampaigns: Campaign[],
): Campaign[] => {
  const linkedIds = new Set(linkedServiceCampaigns.map((c) => c.id));
  return allCampaigns
    .filter(
      (c) =>
        c.id &&
        (BOOSTING_ELIGIBLE_CAMPAIGN_TYPES.includes(c.type) ||
          linkedIds.has(c.id)),
    )
    .sort((a, b) => {
      const linkedDiff =
        Number(linkedIds.has(b.id)) - Number(linkedIds.has(a.id));
      return linkedDiff !== 0 ? linkedDiff : compareAsStrings(a.name, b.name);
    });
};

const buildLinkedServiceRowDrafts = (
  eligibleCampaigns: Campaign[],
  linkedServiceCampaigns: Campaign[],
  boostingId?: string,
): LinkedServiceRowDraft[] => {
  const linkedIds = new Set(linkedServiceCampaigns.map((c) => c.id));
  return eligibleCampaigns.map((c) => ({
    campaignId: c.id ?? "",
    active: linkedIds.has(c.id),
    weight: weightForBoosting(c, boostingId),
  }));
};

const areRowsSame = (
  original: LinkedServiceRowDraft[],
  current: LinkedServiceRowDraft[],
) => {
  const originalById = new Map(original.map((d) => [d.campaignId, d]));
  return (
    original.length === current.length &&
    current.every((d) => {
      const before = originalById.get(d.campaignId);
      return (
        before !== undefined &&
        before.active === d.active &&
        before.weight === d.weight
      );
    })
  );
};

// Pure computation of "what the service campaign's boosting field should become" —
// setting nextWeight links/updates this boosting campaign on it; null unlinks it.
const buildPatchedBoosting = (
  campaign: Campaign,
  nextWeight: number | null,
  boostingCampaign: BoostingCampaign | undefined,
): Boosting => {
  const currentBoosting: Boosting = campaign.boosting ?? {
    boostingWeight: 0,
    linkedBoostingCampaigns: [],
  };
  const withoutThisLink = (
    currentBoosting.linkedBoostingCampaigns ?? []
  ).filter((lbc) => lbc.campaign?.id !== boostingCampaign?.id);
  const linkedBoostingCampaigns =
    nextWeight === null || !boostingCampaign
      ? withoutThisLink
      : [
          ...withoutThisLink,
          { weight: nextWeight, campaign: boostingCampaign },
        ];
  return { ...currentBoosting, linkedBoostingCampaigns };
};

// Pure diff between the original persisted drafts and the current (active-only) drafts:
// which campaign ids dropped out, and which are new or changed weight.
const diffLinkedServiceDrafts = (
  original: LinkedServiceDraft[],
  final: LinkedServiceDraft[],
) => {
  const originalById = new Map(original.map((d) => [d.campaignId, d]));
  const finalIds = new Set(final.map((d) => d.campaignId));
  const removedCampaignIds = original
    .map((d) => d.campaignId)
    .filter((id) => !finalIds.has(id));
  const addedOrChanged = final.filter((d) => {
    const before = originalById.get(d.campaignId);
    return !before || before.weight !== d.weight;
  });
  return { removedCampaignIds, addedOrChanged };
};

export const LinkedServiceCampaignsSection: FC<{
  linkedServiceCampaigns: Campaign[];
  boosting: BoostingCampaign | undefined;
  companyId: string;
  siteId: number;
}> = ({ linkedServiceCampaigns, boosting, companyId, siteId }) => {
  const allCampaigns = useAppSelector((state) => state.campaigns.campaigns);
  const handlerParams = useRequestHandlers();
  const { handleUpsertCampaignData } = handleCampaign(handlerParams, {
    companyId,
    siteId,
  });

  const [showSlideOver, setShowSlideOver] = useState(false);

  const eligibleCampaigns = useMemo(
    () => eligibleCampaignsForRows(allCampaigns, linkedServiceCampaigns),
    [allCampaigns, linkedServiceCampaigns],
  );

  const { search, setSearch, visibleItems } = useSlideOverSearch(
    showSlideOver,
    eligibleCampaigns,
    (c, search) => (c.name ?? "").toLowerCase().includes(search),
  );

  const [drafts, setDrafts] = useState<LinkedServiceRowDraft[]>(() =>
    buildLinkedServiceRowDrafts(
      eligibleCampaigns,
      linkedServiceCampaigns,
      boosting?.id,
    ),
  );

  const initConfig = () =>
    setDrafts(
      buildLinkedServiceRowDrafts(
        eligibleCampaigns,
        linkedServiceCampaigns,
        boosting?.id,
      ),
    );

  const updateDraft = (
    campaignId: string,
    patch: Partial<LinkedServiceRowDraft>,
  ) =>
    setDrafts((prev) =>
      prev.map((d) => (d.campaignId === campaignId ? { ...d, ...patch } : d)),
    );

  const allowSubmit = useMemo(
    () =>
      !areRowsSame(
        buildLinkedServiceRowDrafts(
          eligibleCampaigns,
          linkedServiceCampaigns,
          boosting?.id,
        ),
        drafts,
      ),
    [drafts, eligibleCampaigns, linkedServiceCampaigns, boosting?.id],
  );

  const patchCampaign = (campaign: Campaign, nextWeight: number | null) =>
    handleUpsertCampaignData(campaign, {
      requestType: "campaign",
      dataToUpdate: {
        boosting: buildPatchedBoosting(campaign, nextWeight, boosting),
      },
    });

  const submitFieldHandler = async (): Promise<boolean> => {
    const finalDrafts = drafts
      .filter((d) => d.active)
      .map(({ campaignId, weight }) => ({ campaignId, weight }));
    const { removedCampaignIds, addedOrChanged } = diffLinkedServiceDrafts(
      buildLinkedServiceDrafts(linkedServiceCampaigns, boosting?.id),
      finalDrafts,
    );

    const results = await Promise.all([
      ...removedCampaignIds.map((id) => {
        const campaign = allCampaigns.find((c) => c.id === id);
        return campaign
          ? patchCampaign(campaign, null)
          : Promise.resolve(false);
      }),
      ...addedOrChanged.map((d) => {
        const campaign = allCampaigns.find((c) => c.id === d.campaignId);
        return campaign
          ? patchCampaign(campaign, d.weight)
          : Promise.resolve(false);
      }),
    ]);
    return results.every(Boolean);
  };

  return (
    <Fields title="Linked service campaigns">
      <NamedRow
        title="Linked Service Campaigns"
        customId="linkedServiceCampaignsButton"
        editPermissions={EDIT_PERMISSIONS}
        onClick={() => setShowSlideOver(true)}
        content={linkedServiceCampaigns.length}
      />
      <SlideOverWrapper
        open={showSlideOver}
        initConfig={initConfig}
        onClose={() => setShowSlideOver(false)}
        title={`Linked Service Campaigns (${linkedServiceCampaigns.length})`}
        buttonProps={{ onClick: submitFieldHandler, disabled: !allowSubmit }}
      >
        <SearchBox
          placeholder="Name"
          value={search}
          onChange={({ target }) => setSearch(target.value.toLowerCase())}
        />
        <div className="grid gap-4">
          {visibleItems.map((c) => {
            const draft = drafts.find((d) => d.campaignId === c.id);
            if (!draft) return null;
            return (
              <CollapsedFields
                key={c.id}
                title={`${c.name} (${campaignTypeTitles[c.type]})`}
                active={draft.active}
                permissions={EDIT_PERMISSIONS}
                activateFields={() => updateDraft(c.id ?? "", { active: true })}
                resetFields={() => updateDraft(c.id ?? "", { active: false })}
              >
                <WeightNumberInput
                  value={draft.weight}
                  onChange={(weight) => updateDraft(c.id ?? "", { weight })}
                />
              </CollapsedFields>
            );
          })}
        </div>
      </SlideOverWrapper>
    </Fields>
  );
};
